import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

export interface MenuItem {
  key: string;
  label: string;
  onSelect: () => void;
  /** Red — a destructive item. */
  danger?: boolean;
  /** Ask once more before running it: the first tap turns the item into
   * «Подтвердить», the second runs it (closing the menu forgets the first). */
  confirm?: boolean;
  /** Shown above the item once it is armed — what the action will also do. */
  warning?: string;
  disabled?: boolean;
}

/** A "⋯" button with the secondary actions of a row tucked behind it, so the
 * row keeps only its main action visible.
 *
 * The panel is portalled to <body> and positioned from the button's rect: table
 * wrappers are `overflow-x-auto`, which would clip an absolutely-positioned
 * popup. Closes on outside click, Esc, scroll and resize. */
export function OverflowMenu({
  items,
  label,
  busy,
}: {
  items: MenuItem[];
  label?: string;
  busy?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [armed, setArmed] = useState<string | null>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  const close = () => {
    setOpen(false);
    setArmed(null);
  };

  useLayoutEffect(() => {
    if (!open || !button.current) return;
    const r = button.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panel.current?.contains(target) || button.current?.contains(target)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [open]);

  if (items.length === 0) return null;
  const text = label ?? t("common.more");

  return (
    <>
      <button
        ref={button}
        type="button"
        aria-label={text}
        title={text}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={busy}
        onClick={() => (open ? close() : setOpen(true))}
        className="rounded-md border border-olive-600 px-2 py-1 text-sm leading-none text-sand-muted hover:border-gold hover:text-gold disabled:opacity-50"
      >
        ⋯
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={panel}
            role="menu"
            style={{ position: "fixed", top: pos.top, right: pos.right }}
            className="z-50 min-w-44 rounded-md border border-olive-600 bg-olive-900 py-1 shadow-lg"
          >
            {items.map((item) => {
              const isArmed = armed === item.key;
              return (
                <Fragment key={item.key}>
                {isArmed && item.warning && (
                  <p
                    role="alert"
                    className="mx-2 my-1 max-w-[16rem] rounded-md bg-amber-100 px-2 py-1 text-xs text-amber-900"
                  >
                    {item.warning}
                  </p>
                )}
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  onClick={() => {
                    if (item.confirm && !isArmed) {
                      setArmed(item.key);
                      return;
                    }
                    close();
                    item.onSelect();
                  }}
                  className={
                    "block w-full px-3 py-1.5 text-left text-sm disabled:opacity-50 " +
                    (isArmed
                      ? "bg-red-500/90 text-white hover:bg-red-500"
                      : item.danger
                        ? "text-red-700 hover:bg-red-500/10"
                        : "text-sand hover:bg-olive-700")
                  }
                >
                  {isArmed ? `${t("common.confirm")}: ${item.label}` : item.label}
                </button>
                </Fragment>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}
